import { useEffect, useMemo, useState } from 'react';
import { applySeo, siteUrl } from '../../utils/seo';
import {
    degreesToDegArcmin,
    effectiveFocalLengthMm,
    focalRatio,
    fovDegrees,
    pixelScaleArcsecPerPixel
} from '../../utils/fovMath';
import telescopes from '../../data/gear/telescopes.json';
import cameras from '../../data/gear/cameras.json';
import sensors from '../../data/gear/sensors.json';
import reducers from '../../data/gear/reducers.json';
import TargetSearch from './TargetSearch';
import TargetFrame from './TargetFrame';
import GearSearch from './GearSearch';
import SavedSetups from './SavedSetups';
import { mosaicCsv } from '../../utils/mosaic';
import './fovCalculator.scss';

const reducerEntries = Object.entries(reducers);

// Mosaic planning + CSV export is built but not yet verified against real
// ASIAIR hardware. Flip this to true to bring the UI back once that's
// confirmed; until then it stays out of the way for everyone visiting the
// live tool.
const MOSAIC_FEATURE_ENABLED = false;

const toNumber = (value) => {
    const n = parseFloat(value);
    return Number.isFinite(n) && n > 0 ? n : null;
};

// Starting gear values, also what the admin Clear button in SavedSetups
// resets the form back to.
const DEFAULT_GEAR = {
    telescopeId: 'manual',
    apertureMm: '80',
    focalLengthMm: '480',
    reducerId: 'none',
    reducerFactor: 1,
    cameraId: 'manual',
    pixelSizeUm: '3.76',
    sensorWidthMm: '23.5',
    sensorHeightMm: '15.7'
};

const FovCalculator = () => {
    const [telescopeId, setTelescopeId] = useState(DEFAULT_GEAR.telescopeId);
    const [apertureMm, setApertureMm] = useState(DEFAULT_GEAR.apertureMm);
    const [focalLengthMm, setFocalLengthMm] = useState(DEFAULT_GEAR.focalLengthMm);

    const [reducerId, setReducerId] = useState(DEFAULT_GEAR.reducerId);
    const [reducerFactor, setReducerFactor] = useState(DEFAULT_GEAR.reducerFactor);

    const [cameraId, setCameraId] = useState(DEFAULT_GEAR.cameraId);
    const [pixelSizeUm, setPixelSizeUm] = useState(DEFAULT_GEAR.pixelSizeUm);
    const [sensorWidthMm, setSensorWidthMm] = useState(DEFAULT_GEAR.sensorWidthMm);
    const [sensorHeightMm, setSensorHeightMm] = useState(DEFAULT_GEAR.sensorHeightMm);

    const [target, setTarget] = useState(null);
    const [rotationDeg, setRotationDeg] = useState(0);
    const [rotationText, setRotationText] = useState('0');

    const [mosaicEnabled, setMosaicEnabled] = useState(false);
    const [mosaicRows, setMosaicRows] = useState(2);
    const [mosaicCols, setMosaicCols] = useState(2);
    const [mosaicOverlap, setMosaicOverlap] = useState(10);

    const effectiveMosaicRows = mosaicEnabled ? mosaicRows : 1;
    const effectiveMosaicCols = mosaicEnabled ? mosaicCols : 1;

    useEffect(() => {
        applySeo({
            title: 'FOV calculator | David Joyce Astrophotography',
            description: 'Work out your telescope and camera field of view. Pick your gear or enter the numbers by hand to see your focal length, field of view, and pixel scale.',
            canonicalPath: '/tools/fov-calculator/',
            ogImage: `${siteUrl}/logo192.png`,
            structuredData: {
                '@context': 'https://schema.org',
                '@type': 'WebApplication',
                name: 'FOV calculator',
                applicationCategory: 'UtilitiesApplication',
                url: `${siteUrl}/tools/fov-calculator/`
            }
        });
    }, []);

    const handleTelescopeChange = (id) => {
        setTelescopeId(id);
        if (id !== 'manual' && telescopes[id]) {
            setApertureMm(String(telescopes[id].apertureMm));
            setFocalLengthMm(String(telescopes[id].focalLengthMm));
        }
    };

    const handleReducerChange = (id) => {
        setReducerId(id);
        if (reducers[id]) setReducerFactor(reducers[id].factor);
    };

    const handleCameraChange = (id) => {
        setCameraId(id);
        if (id !== 'manual' && cameras[id]) {
            const sensor = sensors[cameras[id].sensorId];
            if (sensor) {
                setPixelSizeUm(String(sensor.pixelSizeUm));
                setSensorWidthMm(String(sensor.widthMm));
                setSensorHeightMm(String(sensor.heightMm));
            }
        }
    };

    const currentSetup = {
        telescopeId,
        apertureMm,
        focalLengthMm,
        reducerId,
        reducerFactor,
        cameraId,
        pixelSizeUm,
        sensorWidthMm,
        sensorHeightMm
    };

    const handleLoadSetup = (gear) => {
        setTelescopeId(gear.telescopeId);
        setApertureMm(gear.apertureMm);
        setFocalLengthMm(gear.focalLengthMm);
        setReducerId(gear.reducerId);
        setReducerFactor(gear.reducerFactor);
        setCameraId(gear.cameraId);
        setPixelSizeUm(gear.pixelSizeUm);
        setSensorWidthMm(gear.sensorWidthMm);
        setSensorHeightMm(gear.sensorHeightMm);
    };

    const handleDownloadMosaicCsv = () => {
        if (!target || !result) return;
        const { csv } = mosaicCsv({
            raCenter: target.ra,
            decCenter: target.dec,
            fovWidthDeg: result.fovWidth,
            fovHeightDeg: result.fovHeight,
            rotationDeg,
            rows: effectiveMosaicRows,
            cols: effectiveMosaicCols,
            overlapPct: mosaicOverlap
        });
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${target.key || 'mosaic'}-mosaic.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    const result = useMemo(() => {
        const aperture = toNumber(apertureMm);
        const focalLength = toNumber(focalLengthMm);
        const pixelSize = toNumber(pixelSizeUm);
        const sensorWidth = toNumber(sensorWidthMm);
        const sensorHeight = toNumber(sensorHeightMm);

        if (!aperture || !focalLength || !pixelSize || !sensorWidth || !sensorHeight) {
            return null;
        }

        const effFocalLength = effectiveFocalLengthMm(focalLength, reducerFactor);
        const fovW = fovDegrees(sensorWidth, effFocalLength);
        const fovH = fovDegrees(sensorHeight, effFocalLength);

        return {
            effectiveFocalLength: effFocalLength,
            fRatio: focalRatio(effFocalLength, aperture),
            fovWidth: fovW,
            fovHeight: fovH,
            pixelScale: pixelScaleArcsecPerPixel(pixelSize, effFocalLength)
        };
    }, [apertureMm, focalLengthMm, reducerFactor, pixelSizeUm, sensorWidthMm, sensorHeightMm]);

    return (
        <div className='fov-page'>
            <div className='fov-hero'>
                <h1>FOV calculator</h1>
                <p>
                    Pick your telescope and camera, or type in the numbers yourself, and see the
                    focal length, field of view, and pixel scale you will actually image with.
                </p>
            </div>

            <SavedSetups
                currentSetup={currentSetup}
                onLoad={handleLoadSetup}
                onClear={() => handleLoadSetup(DEFAULT_GEAR)}
            />

            <div className='fov-grid'>
                <div className='fov-card'>
                    <p className='fov-card-label'>Telescope</p>
                    <GearSearch
                        id='telescope-select'
                        label='Model'
                        items={telescopes}
                        value={telescopeId}
                        onChange={handleTelescopeChange}
                    />

                    <div className='fov-field-row'>
                        <div>
                            <label className='fov-field-label' htmlFor='aperture-input'>Aperture (mm)</label>
                            <input
                                id='aperture-input'
                                type='number'
                                min='1'
                                value={apertureMm}
                                onChange={(e) => setApertureMm(e.target.value)}
                            />
                        </div>
                        <div>
                            <label className='fov-field-label' htmlFor='focal-length-input'>Focal length (mm)</label>
                            <input
                                id='focal-length-input'
                                type='number'
                                min='1'
                                value={focalLengthMm}
                                onChange={(e) => setFocalLengthMm(e.target.value)}
                            />
                        </div>
                    </div>

                    <label className='fov-field-label' htmlFor='reducer-select'>Reducer / flattener</label>
                    <select
                        id='reducer-select'
                        value={reducerId}
                        onChange={(e) => handleReducerChange(e.target.value)}
                    >
                        {reducerEntries.map(([id, item]) => (
                            <option value={id} key={id}>{item.label}</option>
                        ))}
                    </select>

                    <p className='fov-card-label' style={{ marginTop: '1.5rem' }}>Camera</p>
                    <GearSearch
                        id='camera-select'
                        label='Model'
                        items={cameras}
                        value={cameraId}
                        onChange={handleCameraChange}
                    />

                    <div className='fov-field-row'>
                        <div>
                            <label className='fov-field-label' htmlFor='pixel-size-input'>Pixel size (&micro;m)</label>
                            <input
                                id='pixel-size-input'
                                type='number'
                                min='0.1'
                                step='0.01'
                                value={pixelSizeUm}
                                onChange={(e) => setPixelSizeUm(e.target.value)}
                            />
                        </div>
                        <div>
                            <label className='fov-field-label' htmlFor='sensor-width-input'>Sensor width (mm)</label>
                            <input
                                id='sensor-width-input'
                                type='number'
                                min='0.1'
                                step='0.1'
                                value={sensorWidthMm}
                                onChange={(e) => setSensorWidthMm(e.target.value)}
                            />
                        </div>
                        <div>
                            <label className='fov-field-label' htmlFor='sensor-height-input'>Sensor height (mm)</label>
                            <input
                                id='sensor-height-input'
                                type='number'
                                min='0.1'
                                step='0.1'
                                value={sensorHeightMm}
                                onChange={(e) => setSensorHeightMm(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                <div className='fov-card fov-results'>
                    <p className='fov-card-label'>Results</p>
                    {result ? (
                        <table className='fov-results-table'>
                            <tbody>
                                <tr>
                                    <td>Effective focal length</td>
                                    <td>{result.effectiveFocalLength.toFixed(0)} mm</td>
                                </tr>
                                <tr>
                                    <td>Focal ratio</td>
                                    <td>f/{result.fRatio.toFixed(1)}</td>
                                </tr>
                                <tr>
                                    <td>Field of view</td>
                                    <td>
                                        {degreesToDegArcmin(result.fovWidth)} &times; {degreesToDegArcmin(result.fovHeight)}
                                    </td>
                                </tr>
                                <tr>
                                    <td>Pixel scale</td>
                                    <td>{result.pixelScale.toFixed(2)}&quot;/px</td>
                                </tr>
                            </tbody>
                        </table>
                    ) : (
                        <p className='fov-error'>Enter a value greater than zero for every field to see your results.</p>
                    )}
                </div>
            </div>

            <div className='fov-card fov-target-card'>
                <p className='fov-card-label'>Target</p>
                <TargetSearch onSelect={setTarget} selectedTarget={target} />

                <div className='fov-rotation-row'>
                    <label className='fov-field-label' htmlFor='rotation-input'>
                        Camera rotation ({rotationDeg}&deg;)
                    </label>
                    <div className='fov-rotation-controls'>
                        <input
                            id='rotation-input'
                            className='fov-rotation-slider'
                            type='range'
                            min='0'
                            max='359'
                            step='1'
                            value={rotationDeg}
                            onChange={(e) => {
                                setRotationDeg(Number(e.target.value));
                                setRotationText(e.target.value);
                            }}
                        />
                        <input
                            type='text'
                            inputMode='numeric'
                            className='fov-rotation-number'
                            value={rotationText}
                            onFocus={(e) => e.target.select()}
                            onChange={(e) => {
                                const raw = e.target.value;
                                if (!/^\d{0,3}$/.test(raw)) return;
                                setRotationText(raw);
                                if (raw !== '') setRotationDeg(Math.min(359, parseInt(raw, 10)));
                            }}
                            onBlur={() => {
                                if (rotationText === '') setRotationText(String(rotationDeg));
                                else setRotationText(String(Math.min(359, parseInt(rotationText, 10) || 0)));
                            }}
                        />
                    </div>
                </div>

                {MOSAIC_FEATURE_ENABLED ? (
                    <div className='fov-mosaic-row'>
                        <label className='fov-mosaic-toggle'>
                            <input
                                type='checkbox'
                                checked={mosaicEnabled}
                                onChange={(e) => setMosaicEnabled(e.target.checked)}
                            />
                            Plan a mosaic across multiple panels
                        </label>

                        {mosaicEnabled ? (
                            <>
                                <div className='fov-mosaic-controls'>
                                    <div>
                                        <label className='fov-field-label' htmlFor='mosaic-rows-input'>Rows</label>
                                        <input
                                            id='mosaic-rows-input'
                                            type='number'
                                            min='1'
                                            max='8'
                                            value={mosaicRows}
                                            onChange={(e) => setMosaicRows(Math.min(8, Math.max(1, parseInt(e.target.value, 10) || 1)))}
                                        />
                                    </div>
                                    <div>
                                        <label className='fov-field-label' htmlFor='mosaic-cols-input'>Columns</label>
                                        <input
                                            id='mosaic-cols-input'
                                            type='number'
                                            min='1'
                                            max='8'
                                            value={mosaicCols}
                                            onChange={(e) => setMosaicCols(Math.min(8, Math.max(1, parseInt(e.target.value, 10) || 1)))}
                                        />
                                    </div>
                                    <div>
                                        <label className='fov-field-label' htmlFor='mosaic-overlap-input'>Overlap (%)</label>
                                        <input
                                            id='mosaic-overlap-input'
                                            type='number'
                                            min='0'
                                            max='50'
                                            value={mosaicOverlap}
                                            onChange={(e) => setMosaicOverlap(Math.min(50, Math.max(0, parseInt(e.target.value, 10) || 0)))}
                                        />
                                    </div>
                                </div>
                                <button
                                    type='button'
                                    className='fov-mosaic-download'
                                    disabled={!target || !result}
                                    onClick={handleDownloadMosaicCsv}
                                >
                                    Download mosaic CSV for ASIAIR
                                </button>
                                <p className='fov-mosaic-note'>
                                    {mosaicRows * mosaicCols} panels, plus a center row. The position angle in the
                                    file is this tool&rsquo;s rotation slider value, not an astrometrically calibrated
                                    position angle &mdash; check orientation in ASIAIR before you shoot.
                                </p>
                            </>
                        ) : null}
                    </div>
                ) : null}

                <div className='fov-target-frame-wrap'>
                    <TargetFrame
                        target={target}
                        fovWidthDeg={result ? result.fovWidth : null}
                        fovHeightDeg={result ? result.fovHeight : null}
                        rotationDeg={rotationDeg}
                        mosaicRows={effectiveMosaicRows}
                        mosaicCols={effectiveMosaicCols}
                        mosaicOverlapPct={mosaicOverlap}
                    />
                </div>
            </div>
        </div>
    );
};

export default FovCalculator;
